/**
 * Service Regions Hook
 *
 * Provides service regions data and utilities for determining if a client
 * is outside the service area (suspicious location).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ServiceRegion } from '../lib/api';
import { unwrapApi } from '../lib/unwrapApi';

// =============================================================================
// DEFAULT SERVICE REGIONS (matches backend defaults)
// =============================================================================

const DEFAULT_SERVICE_REGIONS: ServiceRegion[] = [
  {
    id: 'bay-area',
    name: 'Bay Area',
    polygon: [
      [-122.8, 37.1],
      [-122.8, 38.3],
      [-121.4, 38.3],
      [-121.4, 37.1],
      [-122.8, 37.1],
    ],
    color: '#3B82F6',
  },
  {
    id: 'sacramento',
    name: 'Sacramento',
    polygon: [
      [-121.9, 38.2],
      [-121.9, 39.0],
      [-120.8, 39.0],
      [-120.8, 38.2],
      [-121.9, 38.2],
    ],
    color: '#10B981',
  },
];

// =============================================================================
// POINT-IN-POLYGON ALGORITHM
// =============================================================================

/**
 * Check if a point is inside a polygon using the ray casting algorithm.
 *
 * @param lat - Latitude of the point
 * @param lng - Longitude of the point
 * @param polygon - Array of [lng, lat] coordinate pairs defining the polygon
 * @returns true if point is inside the polygon
 */
export function pointInPolygon(
  lat: number,
  lng: number,
  polygon: [number, number][]
): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  const n = polygon.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = polygon[i]; // [lng, lat]
    const [xj, yj] = polygon[j]; // [lng, lat]

    // Check if the ray from (lng, lat) intersects this edge
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if a point is inside ANY of the provided service regions.
 *
 * @param lat - Latitude of the point
 * @param lng - Longitude of the point
 * @param regions - Array of service regions to check against
 * @returns true if point is inside at least one region
 */
export function isPointInServiceArea(
  lat: number,
  lng: number,
  regions: ServiceRegion[]
): boolean {
  const effectiveRegions = regions?.length > 0 ? regions : DEFAULT_SERVICE_REGIONS;
  return effectiveRegions.some(region => pointInPolygon(lat, lng, region.polygon));
}

/**
 * Check if a client's coordinates indicate a suspicious (out-of-area) location.
 *
 * @param latitude - Client's latitude (string or number)
 * @param longitude - Client's longitude (string or number)
 * @param regions - Array of service regions
 * @returns true if the location is suspicious (outside all service regions)
 */
export function isSuspiciousLocation(
  latitude: string | number | null | undefined,
  longitude: string | number | null | undefined,
  regions: ServiceRegion[]
): boolean {
  // No coordinates = can't determine, not suspicious
  if (latitude == null || longitude == null) return false;

  const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

  // Invalid coordinates
  if (isNaN(lat) || isNaN(lng)) return false;

  // Outside all service regions = suspicious
  return !isPointInServiceArea(lat, lng, regions);
}

// =============================================================================
// US FALLBACK REGION (matches backend)
// =============================================================================

const US_FALLBACK_REGION: ServiceRegion = {
  id: 'us-default',
  name: 'United States (Default)',
  polygon: [
    [-125.0, 49.0],   // NW corner
    [-66.0, 49.0],    // NE corner
    [-66.0, 24.0],    // SE corner
    [-125.0, 24.0],   // SW corner
    [-125.0, 49.0],   // Close polygon
  ],
  color: '#9CA3AF',  // Gray to indicate "not configured"
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch and cache service regions configuration for a specific branch.
 * @param branchId - Branch ID (default: 'ssf')
 */
export function useServiceRegions(branchId: string = 'ssf') {
  return useQuery({
    queryKey: ['serviceRegions', branchId],
    queryFn: async () => {
      const response = await api.settings.getServiceRegions(branchId);
      if (!response.success || !response.data) {
        // Return US fallback if API fails
        return { regions: [US_FALLBACK_REGION], branchId, isDefault: true, isFallback: true };
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - matches backend cache
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
  });
}

/**
 * Hook to update all service regions for a branch.
 * @param branchId - Branch ID (default: 'ssf')
 */
export function useUpdateServiceRegions(branchId: string = 'ssf') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (regions: ServiceRegion[]) =>
      api.settings.updateServiceRegions(regions, branchId).then(unwrapApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRegions', branchId] });
    },
  });
}

/**
 * Hook to add a single service region to a branch.
 * @param branchId - Branch ID (default: 'ssf')
 */
export function useAddServiceRegion(branchId: string = 'ssf') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (region: Omit<ServiceRegion, 'color'> & { color?: string }) =>
      api.settings.addServiceRegion(region, branchId).then(unwrapApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRegions', branchId] });
    },
  });
}

/**
 * Hook to delete a service region from a branch.
 * @param branchId - Branch ID (default: 'ssf')
 */
export function useDeleteServiceRegion(branchId: string = 'ssf') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (regionId: string) =>
      api.settings.deleteServiceRegion(regionId, branchId).then(unwrapApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRegions', branchId] });
    },
  });
}

/**
 * Hook that provides a function to check if a location is suspicious.
 *
 * @param branchId - Branch ID (default: 'ssf')
 *
 * Usage:
 * ```tsx
 * const { checkSuspicious, isLoading } = useSuspiciousLocationChecker('ssf');
 * const isSuspicious = checkSuspicious(client.latitude, client.longitude);
 * ```
 */
export function useSuspiciousLocationChecker(branchId: string = 'ssf') {
  const { data, isLoading } = useServiceRegions(branchId);
  const regions = data?.regions || DEFAULT_SERVICE_REGIONS;

  const checkSuspicious = (
    latitude: string | number | null | undefined,
    longitude: string | number | null | undefined
  ): boolean => {
    return isSuspiciousLocation(latitude, longitude, regions);
  };

  return { checkSuspicious, isLoading, regions, branchId };
}
